import type { ApiResponse } from '@libs/api/types';
import type { UserAuthenticationResponse as AuthUserAuthenticationResponse } from '@features/auth/types';
import type {
  AdminUserEducationApplicationResponse,
  AdminLicensePaymentHistoryResponse,
  AdminManualLicenseContractRequest,
  AdminManualLicenseContractResponse,
  AdminTokenHistoryResponse,
  AdminUserDetailResponse,
  AdminUserListParams,
  AdminUserListResponse,
  AdminUserApprovalListResponse,
  AdminUserApprovalParams,
  AboutMeResponse,
  DeviceResponse,
  FindLoginIdRequest,
  GoogleOAuthRequest,
  LogSearchRequest,
  LogSearchResponse,
  OrganizationApprovalStatus,
  RequestPasswordResetRequest,
  ResetPasswordRequest,
  UserAddRequest,
  UserAuthenticationRequest,
  UserAuthenticationResponse,
  UserModifyRequest,
  UserResponse,
  UserVerifyRequest,
} from './types';
import { apiClient } from '@libs/api/client';

const AUTHENTICATE_URL = '/authenticate';
export const USER_URL = '/users';

export const existsByLoginId = async (loginId: string): Promise<ApiResponse<boolean>> => {
  const res = await apiClient.get<ApiResponse<boolean>>('/users/exists/login-id/' + loginId);

  return res.data;
};

export const getAboutMe = async (): Promise<ApiResponse<AboutMeResponse>> => {
  const res = await apiClient.get<ApiResponse<AboutMeResponse>>(`/users/about-me`);
  return res.data;
};

export const getMyDevices = async (): Promise<ApiResponse<DeviceResponse[]>> => {
  const res = await apiClient.get<ApiResponse<DeviceResponse[]>>(`/users/me/devices`);
  return res.data;
};

export const existsByEmail = async (email: string): Promise<ApiResponse<boolean>> => {
  const res = await apiClient.get<ApiResponse<boolean>>('/users/exists/email/' + email);

  return res.data;
};

export const existsByTel = async (tel: string): Promise<ApiResponse<boolean>> => {
  const res = await apiClient.get<ApiResponse<boolean>>('/users/exists/tel/' + tel);

  return res.data;
};

// 20250610, 권의진, 토큰으로 유저찾기
export const getUserByUid = async (userVerify: UserVerifyRequest): Promise<ApiResponse<boolean>> => {
  const res = await apiClient.post<ApiResponse<boolean>>(USER_URL + '/verify-password', userVerify);
  return res.data;
};

export const register = async (userData: UserAddRequest): Promise<UserResponse> => {
  const res = await apiClient.post<UserResponse>(USER_URL, userData);
  return res.data;
};

export const modify = async (userData: UserModifyRequest): Promise<UserResponse> => {
  const res = await apiClient.put<UserResponse>(USER_URL, userData);
  return res.data;
};

export const remove = async (userId: number): Promise<ApiResponse<boolean>> => {
  const res = await apiClient.delete<ApiResponse<boolean>>(`/users/${userId}`);
  return res.data;
};

export const removeDevice = async (deviceId: string): Promise<ApiResponse<boolean>> => {
  console.log('removeDevice', deviceId);
  const res = await apiClient.delete<ApiResponse<boolean>>(`/users/devices/${deviceId}`);
  return res.data;
};

export const signIn = async (userData: UserAuthenticationRequest): Promise<ApiResponse<UserAuthenticationResponse>> => {
  const response = await apiClient.post<ApiResponse<UserAuthenticationResponse>>('/login', userData);
  return response.data;
};

export const exchangeExternalLoginKey = async (key: string): Promise<ApiResponse<AuthUserAuthenticationResponse>> => {
  const response = await apiClient.post<ApiResponse<AuthUserAuthenticationResponse>>(
    '/external-api/auto-web-login/exchange',
    { key },
  );
  return response.data;
};

export const signOut = async () => {
  return await apiClient.post<ApiResponse<string>>('/logout', null);
};

// 구글 OAuth 로그인
export const signInWithGoogle = async (
  request: GoogleOAuthRequest,
): Promise<ApiResponse<UserAuthenticationResponse>> => {
  const response = await apiClient.post<ApiResponse<UserAuthenticationResponse>>('/auth/google', request);
  return response.data;
};

// 리프레시 토큰을 사용하여 재로그인 시도
export const refreshToken = async (): Promise<ApiResponse<UserAuthenticationResponse>> => {
  const response = await apiClient.post<ApiResponse<UserAuthenticationResponse>>(AUTHENTICATE_URL + '/refresh', null);
  return response.data;
};

// 이메일 인증 코드 발송
export const sendVerificationCode = async (email: string): Promise<ApiResponse<boolean>> => {
  const res = await apiClient.post<ApiResponse<boolean>>(USER_URL + '/send-verification-code', { email });
  return res.data;
};

// 이메일 인증 코드 확인
export const verifyEmailCode = async (email: string, verificationCode: string): Promise<ApiResponse<boolean>> => {
  const res = await apiClient.post<ApiResponse<boolean>>(USER_URL + '/verify-email-code', {
    email,
    verificationCode,
  });
  return res.data;
};

// ID 찾기
export const findLoginId = async (request: FindLoginIdRequest): Promise<ApiResponse<boolean>> => {
  const res = await apiClient.post<ApiResponse<boolean>>(USER_URL + '/find-login-id', request);
  return res.data;
};

// 비밀번호 재설정 요청
export const requestPasswordReset = async (request: RequestPasswordResetRequest): Promise<ApiResponse<boolean>> => {
  const res = await apiClient.post<ApiResponse<boolean>>(USER_URL + '/request-password-reset', request);
  return res.data;
};

// 비밀번호 재설정
export const resetPassword = async (request: ResetPasswordRequest): Promise<ApiResponse<boolean>> => {
  const res = await apiClient.post<ApiResponse<boolean>>(USER_URL + '/reset-password', request);
  return res.data;
};

// 어드민 사용자 목록 조회
export const getAdminUsers = async (params: AdminUserListParams = {}): Promise<ApiResponse<AdminUserListResponse>> => {
  const queryParams = new URLSearchParams();

  if (params.keyword) queryParams.append('keyword', params.keyword);
  if (params.sellApplyStatus) queryParams.append('sellApplyStatus', params.sellApplyStatus);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection);
  if (params.page !== undefined) queryParams.append('page', params.page.toString());
  if (params.size !== undefined) queryParams.append('size', params.size.toString());
  if (params.sort) queryParams.append('sort', params.sort);

  const res = await apiClient.get<ApiResponse<AdminUserListResponse>>(`/admin/users?${queryParams.toString()}`);
  return res.data;
};

// 어드민 사용자 상세 조회
export const getAdminUserDetail = async (userId: number): Promise<ApiResponse<AdminUserDetailResponse>> => {
  const res = await apiClient.get<ApiResponse<AdminUserDetailResponse>>(`/admin/users/${userId}`);
  return res.data;
};

// 어드민 사용자별 토큰 이력 조회
export const getAdminUserTokenHistory = async (
  userId: number,
  page: number = 0,
  size: number = 20,
  sort: string = 'createdAt,desc',
  groupByToken: boolean = false,
): Promise<ApiResponse<AdminTokenHistoryResponse>> => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sort,
    groupByToken: groupByToken.toString(),
  });
  const res = await apiClient.get<ApiResponse<AdminTokenHistoryResponse>>(
    `/admin/users/${userId}/token-history?${queryParams.toString()}`,
  );
  return res.data;
};

// 어드민 사용자별 라이센스 결제 내역 조회
export const getAdminUserLicensePaymentHistory = async (
  userId: number,
  page: number = 0,
  size: number = 20,
  sort: string = 'createdAt,desc',
): Promise<ApiResponse<AdminLicensePaymentHistoryResponse>> => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sort,
  });
  const res = await apiClient.get<ApiResponse<AdminLicensePaymentHistoryResponse>>(
    `/admin/users/${userId}/license-payment-history?${queryParams.toString()}`,
  );
  return res.data;
};

export const getAdminUserEducationApplications = async (
  userId: number,
): Promise<ApiResponse<AdminUserEducationApplicationResponse[]>> => {
  const res = await apiClient.get<ApiResponse<AdminUserEducationApplicationResponse[]>>(
    `/admin/users/${userId}/education-applications`,
  );
  return res.data;
};

export const postAdminManualLicenseContract = async (
  userId: number,
  request: AdminManualLicenseContractRequest,
): Promise<ApiResponse<AdminManualLicenseContractResponse>> => {
  const res = await apiClient.post<ApiResponse<AdminManualLicenseContractResponse>>(
    `/admin/users/${userId}/licenses/manual-contract`,
    request,
  );
  return res.data;
};

// 관리자용 유저별 로그 검색 API
export const getAdminUserLogs = async (request: LogSearchRequest): Promise<ApiResponse<LogSearchResponse>> => {
  const res = await apiClient.post<ApiResponse<LogSearchResponse>>(`/admin/users/${request.userId}/logs`, request);
  return res.data;
};

// 기관 승인 대기 유저 목록 조회
export const getAdminUserApprovals = async (
  params: AdminUserApprovalParams = {},
): Promise<ApiResponse<AdminUserApprovalListResponse>> => {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.search) queryParams.append('search', params.search);
  if (params.page !== undefined) queryParams.append('page', params.page.toString());
  if (params.size !== undefined) queryParams.append('size', params.size.toString());
  const res = await apiClient.get<ApiResponse<AdminUserApprovalListResponse>>(
    `/admin/users/approvals?${queryParams.toString()}`,
  );
  return res.data;
};

// 기관 유저 승인/거절
export const updateAdminUserApproval = async (
  userId: number,
  status: OrganizationApprovalStatus,
  licenseRequest?: { licenseItemId: number; endDate: string; amount?: number },
): Promise<ApiResponse<boolean>> => {
  const res = await apiClient.put<ApiResponse<boolean>>(
    `/admin/users/${userId}/approval?status=${status}`,
    licenseRequest ?? {},
  );
  return res.data;
};
